import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { apiFetch, apiPost, friendlyError, type ApiError } from "../../api/client.js";
import { QuotaBar } from "./quota-bar.js";
import type { ContainerDetail, ContainerListItem } from "./containerUtils.js";

interface ContainerOptions {
  quota: { cpu_cores: number; ram_mb: number; disk_gb: number };
  usage: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
  usage_unavailable?: boolean;
  remaining: { cpu_cores: number; ram_mb: number; disk_gb: number } | null;
}

interface ResizeContainerModalProps {
  opened: boolean;
  onClose: () => void;
  container: ContainerListItem | ContainerDetail;
  onResized: () => void;
}

export function ResizeContainerModal({
  opened,
  onClose,
  container,
  onResized,
}: ResizeContainerModalProps) {
  const current = {
    cores: container.cpu_cores,
    memory_mb: container.memory_mb,
    swap_mb: container.swap_mb,
    disk_gb: Math.round(container.disk_total_bytes / 1024 ** 3),
  };
  const vmid = container.vmid;

  const [options, setOptions] = useState<ContainerOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [cores, setCores] = useState<number | "">("");
  const [memoryMb, setMemoryMb] = useState<number | "">("");
  const [swapMb, setSwapMb] = useState<number | "">("");
  const [diskDelta, setDiskDelta] = useState<number | "">("");
  const [memUnit, setMemUnit] = useState<"mb" | "gb">("gb");
  const [swapUnit, setSwapUnit] = useState<"mb" | "gb">("gb");

  async function loadOptions() {
    setLoading(true);
    try {
      const data = await apiFetch<ContainerOptions>("/api/container-options");
      setOptions(data);
      setCores(current.cores);
      setMemoryMb(current.memory_mb);
      setSwapMb(current.swap_mb);
      setDiskDelta(0);
      setMemUnit("gb");
      setSwapUnit("gb");
    } catch (err) {
      notifications.show({
        color: "red",
        message: friendlyError(err, "Could not load resize options."),
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (opened) {
      void loadOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const usageUnavailable = !!options?.usage_unavailable;
  const usage = options?.usage ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };
  const remaining = options?.remaining ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };
  const quota = options?.quota ?? { cpu_cores: 0, ram_mb: 0, disk_gb: 0 };

  const newCores = typeof cores === "number" ? cores : current.cores;
  const newMemoryMb = typeof memoryMb === "number" ? memoryMb : current.memory_mb;
  const newSwapMb = typeof swapMb === "number" ? swapMb : current.swap_mb;
  const newDelta = typeof diskDelta === "number" ? diskDelta : 0;

  const maxCores = Math.min(64, quota.cpu_cores);
  const maxRamMb = Math.min(262144, current.memory_mb + current.swap_mb + remaining.ram_mb);
  const maxDiskDelta = remaining.disk_gb;

  // Projected totals: subtract THIS container's current contribution
  // (memory+swap + disk) from cluster usage, then add the new values.
  const projectedRam =
    usage.ram_mb - (current.memory_mb + current.swap_mb) + (newMemoryMb + newSwapMb);
  const projectedDisk = usage.disk_gb + newDelta;

  const coresChanged = typeof cores === "number" && cores !== current.cores;
  const memChanged = typeof memoryMb === "number" && memoryMb !== current.memory_mb;
  const swapChanged = typeof swapMb === "number" && swapMb !== current.swap_mb;
  const diskChanged = typeof diskDelta === "number" && diskDelta !== 0;
  const changed = coresChanged || memChanged || swapChanged || diskChanged;

  const overCores = newCores > quota.cpu_cores;
  const overRam = projectedRam > quota.ram_mb;
  const overDisk = projectedDisk > quota.disk_gb;

  const canSubmit =
    !submitting &&
    !usageUnavailable &&
    changed &&
    !overCores &&
    !overRam &&
    !overDisk;

  async function submit() {
    if (!canSubmit || vmid === null) return;
    setSubmitting(true);
    const body: Record<string, unknown> = {};
    if (coresChanged) body.cores = newCores;
    if (memChanged) body.memory_mb = newMemoryMb;
    if (swapChanged) body.swap_mb = newSwapMb;
    if (diskChanged) body.disk_gb_delta = newDelta;
    try {
      await apiPost<{ vmid: number }>(`/api/containers/${vmid}/resize`, body);
      notifications.show({ color: "green", message: "Container resized." });
      onResized();
      onClose();
    } catch (err) {
      const e = err as ApiError;
      const detail = e.details as Record<string, unknown> | undefined;
      if (e.status === 400 && e.error === "no_change") {
        notifications.show({ color: "red", message: "No changes to apply." });
      } else if (e.status === 409 && e.error === "quota_exceeded") {
        const excess = detail?.excess as Record<string, number> | undefined;
        const fields: string[] = [];
        if (excess?.cpu_cores) fields.push(`CPU +${excess.cpu_cores}`);
        if (excess?.ram_mb) fields.push(`RAM +${excess.ram_mb} MB`);
        if (excess?.disk_gb) fields.push(`Disk +${excess.disk_gb} GB`);
        notifications.show({
          color: "red",
          message: `Not enough quota — ${fields.join(", ") || "insufficient headroom"}.`,
        });
      } else if (e.status === 502 && e.error === "pve_unavailable" && !!detail?.partial) {
        notifications.show({
          color: "orange",
          message: "Disk resized; hardware update failed. Refresh to see current state.",
        });
        onResized();
        onClose();
      } else if (e.status === 502 && e.error === "pve_unavailable") {
        notifications.show({
          color: "red",
          message: "Proxmox is unavailable. Please try again.",
        });
      } else if (e.status === 429) {
        notifications.show({ color: "red", message: "Too many resize attempts. Wait a minute." });
      } else {
        notifications.show({ color: "red", message: friendlyError(e, "Resize failed.") });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const memDisplay =
    memUnit === "gb" && typeof memoryMb === "number" ? memoryMb / 1024 : memoryMb;
  const swapDisplay =
    swapUnit === "gb" && typeof swapMb === "number" ? swapMb / 1024 : swapMb;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Resize container"
      size="lg"
      closeOnClickOutside={!submitting}
      closeOnEscape={!submitting}
    >
      <Stack gap="md">
        {loading && (
          <Text c="dimmed" size="sm">
            Loading options…
          </Text>
        )}

        {options && usageUnavailable && (
          <Alert color="yellow" variant="light" p="sm">
            Can&apos;t compute current usage (Proxmox unreachable). Please try again later.
          </Alert>
        )}

        {options && !usageUnavailable && (
          <>
            <Alert color="gray" variant="light" p="sm">
              <Stack gap="xs">
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Projected usage after resize
                </Text>
                <QuotaBar label="RAM (memory + swap)" used={projectedRam} total={quota.ram_mb} unit="MB" />
                <QuotaBar label="Disk" used={projectedDisk} total={quota.disk_gb} unit="GB" />
              </Stack>
            </Alert>

            <NumberInput
              label="CPU cores"
              description={`Current: ${current.cores}`}
              value={cores}
              onChange={(v) => setCores(typeof v === "number" ? v : "")}
              min={1}
              max={maxCores}
              error={overCores ? `Up to ${maxCores} cores per container.` : undefined}
              disabled={submitting}
            />

            <Stack gap={4}>
              <Group justify="space-between" align="flex-end">
                <Box style={{ flex: 1 }}>
                  <NumberInput
                    label={memUnit === "gb" ? "Memory (GB)" : "Memory (MB)"}
                    description={`Current: ${formatRamBrief(current.memory_mb)}`}
                    value={memDisplay}
                    onChange={(v) => {
                      const n = typeof v === "number" ? v : 0;
                      setMemoryMb(memUnit === "gb" ? Math.round(n * 1024) : n);
                    }}
                    min={memUnit === "gb" ? 1 : 128}
                    max={
                      memUnit === "gb"
                        ? Math.min(256, Math.floor(maxRamMb / 1024) || 1)
                        : Math.min(262144, Math.max(128, maxRamMb))
                    }
                    error={overRam ? `RAM quota allows ${maxRamMb} MB total (memory + swap).` : undefined}
                    disabled={submitting}
                  />
                </Box>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => setMemUnit((u) => (u === "gb" ? "mb" : "gb"))}
                  disabled={submitting}
                >
                  Switch to {memUnit === "gb" ? "MB" : "GB"}
                </Button>
              </Group>
            </Stack>

            <Stack gap={4}>
              <Group justify="space-between" align="flex-end">
                <Box style={{ flex: 1 }}>
                  <NumberInput
                    label={swapUnit === "gb" ? "Swap (GB)" : "Swap (MB)"}
                    description={`Current: ${formatRamBrief(current.swap_mb)}`}
                    value={swapDisplay}
                    onChange={(v) => {
                      const n = typeof v === "number" ? v : 0;
                      setSwapMb(swapUnit === "gb" ? Math.round(n * 1024) : n);
                    }}
                    min={0}
                    max={swapUnit === "gb" ? Math.floor(maxRamMb / 1024) : maxRamMb}
                    disabled={submitting}
                  />
                </Box>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={() => setSwapUnit((u) => (u === "gb" ? "mb" : "gb"))}
                  disabled={submitting}
                >
                  Switch to {swapUnit === "gb" ? "MB" : "GB"}
                </Button>
              </Group>
            </Stack>

            <NumberInput
              label="Add disk (GB)"
              description={`Current disk: ${current.disk_gb} GB`}
              value={diskDelta}
              onChange={(v) => setDiskDelta(typeof v === "number" ? v : "")}
              min={0}
              max={maxDiskDelta}
              error={overDisk ? `Only ${maxDiskDelta} GB remaining.` : undefined}
              disabled={submitting}
            />

            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={() => void submit()} loading={submitting} disabled={!canSubmit}>
                Apply
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

function formatRamBrief(mb: number): string {
  if (mb >= 1024 && mb % 1024 === 0) return `${mb / 1024} GB`;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}
